/* eslint-disable react/sort-comp */
/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { PureComponent, RefObject } from 'react';
import {
  Seconds,
  NagraSettings,
  DrmSystem,
  ArrayBufferUtils,
  ShakaSettings,
  Specs,
  TimeUtils,
  AssetUtils,
  Asset,
  PlayerErrors,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { NagraPlayerEvent } from 'types/libs/opySdk';
import PlatformUtils, { APP_BROWSERS } from 'services/utils/PlatformUtils';
import { IPlayerStats } from '../VideoUi/Stats';

export enum MediaErrorCodes {
  MEDIA_ERR_CUSTOM = 0,
  MEDIA_ERR_ABORTED = 1,
  MEDIA_ERR_NETWORK = 2,
  MEDIA_ERR_DECODE = 3,
  MEDIA_ERR_SRC_NOT_SUPPORTED = 4,
  MEDIA_ERR_ENCRYPTED = 5,
}

const VIDEO_ELEMENT_ID = 'Nagra_videoPlayer';
const browser = PlatformUtils.getBrowser();
// set the default mime type regarding the Nagra API docs
const defaultMimeType =
  PlatformUtils.getBrowser() === APP_BROWSERS.Safari ? 'application/vnd.apple.mpegurl' : 'application/dash+xml';

export interface SsmSettings {
  licenseHandler: (
    drmId: string,
    challenge: string,
    isRenewal: boolean
  ) => Promise<{ license: string; heartbeat: number }>;
  certificateHandler: (licenseServerUrl: string, tenantId: string, drmSystem: DrmSystem) => Promise<Uint8Array>;
}

export interface StreamRange {
  start: Seconds;
  end: Seconds;
  isEpoch: boolean;
}

interface Props {
  videoElementRef?: RefObject<HTMLDivElement>;
  nagraSettings?: NagraSettings;
  shakaSettings?: ShakaSettings;
  ssmSettings?: SsmSettings;
  onStreamReady?: () => void;
  onPositionUpdate?: (progress: Seconds) => void;
  onBuffering?: () => void;
  onPlaying?: () => void;
  onError?: (error?: XError) => void;
  onEnd?: () => void;
  onPaused?: () => void;
  onAutoPlayDenied?: (denied: boolean) => void;
  onPlay?: () => void;
  onBitrateChange?: (bitrate: number) => void;
  onMute?: (muted?: boolean) => void;
}

const DEFAULT_RANGE: StreamRange = {
  start: 0,
  end: 0,
  isEpoch: false,
};

class Nagra extends PureComponent<Props> {
  private playerInstance: Otvplayer | undefined;

  private prevPosition: Seconds = 0;

  private preferredMimeType: string | undefined;

  private setSrcRetry = false;

  private videoSrc: VideoSrc | undefined;

  private streamReady = false;

  private reportPositionTimer: ReturnType<typeof setInterval> | undefined;

  componentWillUnmount(): void {
    this.playerInstance?.dispose();
    this.playerInstance = undefined;

    this.cancelPositionTimer();
  }

  // PLAYER INITIALIZATION

  private async createPlayerInstance(): Promise<void> {
    const drmConfigs = await this.getDrmConfigs();

    return new Promise((resolve, reject) => {
      this.playerInstance = otvplayer(
        VIDEO_ELEMENT_ID,
        {
          html5: {
            nativeCaptions: browser === APP_BROWSERS.Safari,
            nativeAudioTracks: browser === APP_BROWSERS.Safari,
          },
          muted: true,
          autoplay: true,
          nativeControlsForTouch: false,
          plugins: {
            otvtoolkit: {
              creationFailureCallback: () => {
                reject(new Error('Error creating player')); // TODO: fix this error
              },
              drm: drmConfigs,
            },
          },
        },
        () => {
          this.setPlayerEventListeners();
          resolve();
        }
      );
    });
  }

  private getSSPOptions(): OtvDrmConfigs {
    const { nagraSettings } = this.props;

    const configs: OtvDrmConfigs = {
      config: {
        tenantId: nagraSettings?.tenantId,
        licenceServer: nagraSettings?.licenseServerUrl,
      },
    };

    if (browser === APP_BROWSERS.Safari) {
      configs.system = 'nagra-ssp-fps';
    } else {
      configs.system = 'nagra-ssp';
      configs.config.mode = 'token';
    }

    return configs;
  }

  private getStreamingShakaSettings(): ShakaSettingsStreaming {
    const { shakaSettings } = this.props;

    const configs = {
      rebufferingGoal: shakaSettings?.rebufferingGoal,
      bufferingGoal: shakaSettings?.bufferingGoal,
      bufferBehind: shakaSettings?.bufferBehind,
      ignoreTextStreamFailures: shakaSettings?.ignoreTextStreamFailures,
      stallEnabled: shakaSettings?.stallEnabled,
      stallThreshold: shakaSettings?.stallThreshold,
      stallSkip: shakaSettings?.stallSkip,
    };

    return configs;
  }

  private getAbrShakaSettings(): ShakaSettingsAbr {
    const { shakaSettings } = this.props;

    const configs: ShakaSettingsAbr = {
      defaultBandwidthEstimate:
        shakaSettings?.defaultBandwidthEstimate !== undefined
          ? shakaSettings?.defaultBandwidthEstimate
          : Specs.DMS_SHAKA_DEFAULT_BANDWIDTH_ESTIMATE,
    };

    return configs;
  }

  private getManifestShakaSettings(): ShakaSettingsManifest {
    const { shakaSettings } = this.props;
    const configs = {
      availabilityWindowOverride: Infinity,
      defaultPresentationDelay:
        shakaSettings?.defaultPresentationDelay !== undefined
          ? shakaSettings?.defaultPresentationDelay
          : Specs.DMS_SHAKA_DEFAULT_PRESENTATION_DELAY,
    };

    return configs;
  }

  private getRestrictionsShakaSettings(): BandwitdhRestrictions {
    const { shakaSettings } = this.props;

    const configs = {
      minBandwidth: shakaSettings?.minBandwidth,
      maxBandwidth: shakaSettings?.maxBandwidth,
    };

    return configs;
  }

  private async getSSMOptions(): Promise<OtvDrmConfigs> {
    const { ssmSettings, nagraSettings } = this.props;

    if (ssmSettings && nagraSettings) {
      const configs: OtvDrmConfigs = {
        system: 'customer-ssm',
        config: {
          customerSsmCallback: {
            getLicence: async (
              drmId: string,
              challenge: ArrayBuffer,
              requestType: LicenseRequestType
            ): Promise<{ licence: Uint8Array; heartbeat: number }> => {
              if (ssmSettings?.licenseHandler) {
                const res = await ssmSettings.licenseHandler(
                  drmId,
                  ArrayBufferUtils.arrayBufferToBase64(challenge),
                  requestType === 'license-renewal'
                );
                return {
                  licence: ArrayBufferUtils.base64ToBytes(res.license),
                  heartbeat: res.heartbeat,
                };
              }

              return Promise.reject(new Error('No licenseHandler available!'));
            },
            heartbeat: () => {
              // do nothing
            },
          },
        },
      };

      const certificateRes = await ssmSettings.certificateHandler(
        nagraSettings.licenseServerUrl,
        nagraSettings.tenantId,
        browser === APP_BROWSERS.Safari ? DrmSystem.FAIRPLAY : DrmSystem.WIDEVINE
      );

      if (browser === APP_BROWSERS.Safari) {
        configs.config.fairplayServerCertificate = certificateRes;
      } else {
        configs.config.widevineServerCertificate = ArrayBufferUtils.arrayBufferToBase64(certificateRes);
      }

      return configs;
    }

    return Promise.reject(new Error('No SSM configs available!'));
  }

  private async getDrmConfigs(): Promise<OtvDrmConfigs> {
    const { ssmSettings } = this.props;

    const configs: OtvDrmConfigs = ssmSettings ? await this.getSSMOptions() : this.getSSPOptions();

    if (browser === APP_BROWSERS.Safari) {
      configs.config.persistentState = 'required';
    } else {
      configs.config['com.widevine.alpha'] = {
        persistentState: 'required',
      };
    }

    // If the browser is Edge (Chromium), set Widevine as preferred DRM instead of PlayReady
    if (browser === APP_BROWSERS.EdgeChromium) {
      configs.config.preferredDRM = 'com.widevine.alpha';
    }

    return configs;
  }

  private cancelPositionTimer(): void {
    if (this.reportPositionTimer) {
      clearInterval(this.reportPositionTimer);
      this.reportPositionTimer = undefined;
    }
  }

  private handlePositionUpdateEvents(): void {
    const { onPositionUpdate } = this.props;
    if (onPositionUpdate) {
      this.reportPositionTimer = setInterval(() => {
        const newPos = this.getPosition();
        if (newPos !== this.prevPosition) {
          onPositionUpdate(newPos);
          this.prevPosition = newPos;
        }
      }, 500); // in order the have a better precision
    }
  }

  private setPlayerEventListeners(): void {
    const { onStreamReady, onError, onEnd, onPaused, onBuffering, onPlaying, onPlay, onBitrateChange } = this.props;

    if (onStreamReady) {
      this.playerInstance?.on(NagraPlayerEvent.LOADED_DATA, () => {
        this.streamReady = true;
        onStreamReady();
        this.handlePositionUpdateEvents();
      });
    }

    if (onBuffering) {
      this.playerInstance?.on(NagraPlayerEvent.WAITING, onBuffering);
    }

    if (onPlaying) {
      this.playerInstance?.on(NagraPlayerEvent.PLAYING, onPlaying);
    }

    if (onPaused) {
      this.playerInstance?.on(NagraPlayerEvent.PAUSE, onPaused);
    }

    if (onPlay) {
      this.playerInstance?.on(NagraPlayerEvent.PLAY, onPlay);
    }

    if (onError) {
      this.playerInstance?.on(NagraPlayerEvent.ERROR, () => {
        const err = this.getError();

        if (
          err?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED &&
          !this.streamReady &&
          this.videoSrc?.type !== undefined &&
          !this.setSrcRetry
        ) {
          // this error is raised when the content is not supported or mimeType doesn't match stream

          // eslint-disable-next-line no-console
          console.warn(
            `Probably the mimetype "${this.videoSrc.type}" don't match the stream. Trying without set the mimetype.`
          );

          this.setSrcRetry = true;

          // reload the src without set the mimeType
          delete this.videoSrc.type;

          this.playerInstance?.src(this.videoSrc);

          return;
        }

        if (err?.code === MediaError.MEDIA_ERR_NETWORK && this.streamReady) {
          // ignore network errors if stream was loaded
          // eslint-disable-next-line no-console
          console.warn(
            'Network error detected by the player will be ignored as the connection loss is already handled by the app.'
          );
          return;
        }

        // if no error saved in the error ref, fallback to the default
        let errorCode: string | undefined;
        switch (err?.code) {
          case MediaErrorCodes.MEDIA_ERR_CUSTOM:
            errorCode = PlayerErrors.play.custom;
            break;
          case MediaErrorCodes.MEDIA_ERR_ABORTED:
            errorCode = PlayerErrors.play.aborted;
            break;
          case MediaErrorCodes.MEDIA_ERR_NETWORK:
            errorCode = PlayerErrors.play.network;
            break;
          case MediaErrorCodes.MEDIA_ERR_DECODE:
            errorCode = PlayerErrors.play.decode;
            break;
          case MediaErrorCodes.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorCode = PlayerErrors.play.scr_not_supported;
            break;
          case MediaErrorCodes.MEDIA_ERR_ENCRYPTED:
            errorCode = PlayerErrors.play.encrypted;
            break;
          default:
            errorCode = undefined;
        }

        if (errorCode) {
          onError(XError.fromCode(errorCode));
        } else {
          onError();
        }
      });
    }

    if (onEnd) {
      this.playerInstance?.on(NagraPlayerEvent.ENDED, onEnd);
    }

    if (onBitrateChange) {
      this.playerInstance?.otvtoolkit().networkStatistics.addNetworkListener({
        selectedBitrateChanged: onBitrateChange,
      });
    }
  }

  // PLAYER API

  setPreferredMimeType(mimeType: string | undefined): void {
    this.preferredMimeType = mimeType;
  }

  async setStream(src: string, token?: string, muted?: boolean, loop?: boolean): Promise<void> {
    try {
      if (!this.playerInstance) {
        await this.createPlayerInstance();
      }

      this.cancelPositionTimer();

      this.videoSrc = {
        src,
        type: this.preferredMimeType || defaultMimeType,
        token,
      };

      this.setSrcRetry = false;
      this.streamReady = false;

      const mimeTypeNotSupported =
        this.videoSrc.type !== undefined &&
        this.videoSrc.type !== defaultMimeType && // exclude the default because in chrome canPlayType(defaultMimeType) returns ''!
        this.playerInstance?.canPlayType(this.videoSrc.type) === '';

      if (mimeTypeNotSupported) {
        // eslint-disable-next-line no-console
        console.warn(
          `The mimetype "${this.videoSrc.type}" is not supported by this browser. Trying without set the mimetype.`
        );
        delete this.videoSrc.type;
      }

      this.playerInstance?.src(this.videoSrc);

      if (muted !== undefined) {
        this.setMuted(muted);
      } else if (!this.isMuted()) {
        // direct call to muted, don't use the setMuted function to keep this temporary mute value away from the LocalStore
        this.playerInstance?.muted(true);
      }

      if (loop !== undefined) {
        this.playerInstance?.loop(loop);
      }
    } catch {
      // eslint-disable-next-line no-console
      console.warn('Something wrong happen while creating player instance');
      // be sure the promise is resolved
      // sometimes player raises exceptions although it keeps working
    }
  }

  updatePlayerSettings(): void {
    try {
      this.playerInstance?.otvtoolkit().configure({
        abr: this.getAbrShakaSettings(),
        manifest: this.getManifestShakaSettings(),
        streaming: this.getStreamingShakaSettings(),
        // Active Restrictions on the player side.
        // https://cps.jira.agile.vodafone.com/browse/VTAS-1112
        restrictions: this.getRestrictionsShakaSettings(),
      });
    } catch (e) {
      // ignore: this statement can trow an «TypeError: can't access property "drm", e is null»
      // despite this error the player keeps playing, so ignore it.
    }
  }

  getPlayerStats() {
    let statsObj: IPlayerStats;

    try {
      // Only for Chromium based browsers
      const jsStats = window.performance.memory;

      const stats = this.playerInstance?.otvtoolkit().techConfig?.tech?.getStats() || undefined;
      const bufferedInfo = this.playerInstance?.otvtoolkit().techConfig?.tech?.getBufferedInfo() || undefined;

      /* Other metrics available in
      this.playerInstance?.otvtoolkit().techConfig.tech?.keySystem();
      this.playerInstance?.otvtoolkit().techConfig.tech?.getVariantTracks()
      this.playerInstance?.otvtoolkit().techConfig.tech?.getBufferFullness()
      */

      statsObj = {
        currentTime: this.getPosition(),
        bufferedTotalEnd: bufferedInfo?.total.length ? bufferedInfo?.total[0].end : undefined,
        bufferedAudioEnd: bufferedInfo?.audio.length ? bufferedInfo?.audio[0].end : undefined,
        bufferedTextEnd: bufferedInfo?.text.length ? bufferedInfo?.text[0].end : undefined,
        bufferedVideoEnd: bufferedInfo?.video.length ? bufferedInfo?.video[0].end : undefined,
        bufferingTime: stats?.bufferingTime.toString() || undefined,
        droppedFrames: stats?.droppedFrames.toString() || undefined,
        licenseTime: stats?.licenseTime.toString() || undefined,
        estimatedBandwidth: stats?.estimatedBandwidth || undefined,
        streamBandwidth: stats?.streamBandwidth || undefined,
        resolution: stats?.height && stats.width ? `${stats?.width} x ${stats?.height} px` : undefined,
        jsHeapSizeLimit:
          browser === APP_BROWSERS.Chrome || browser === APP_BROWSERS.EdgeChromium
            ? jsStats.jsHeapSizeLimit
            : undefined,
        totalJSHeapSize:
          browser === APP_BROWSERS.Chrome || browser === APP_BROWSERS.EdgeChromium
            ? jsStats.totalJSHeapSize
            : undefined,
        usedJSHeapSize:
          browser === APP_BROWSERS.Chrome || browser === APP_BROWSERS.EdgeChromium ? jsStats.usedJSHeapSize : undefined,
      };

      return statsObj;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ERROR getPlayerStats: ', error);
    }
    return {};
  }

  pause(): void {
    this.playerInstance?.pause();
  }

  stop(): void {
    this.playerInstance?.pause();
    this.playerInstance?.currentTime(0);
  }

  play(): void {
    const { onAutoPlayDenied } = this.props;

    if (onAutoPlayDenied) {
      onAutoPlayDenied(false);
    }
    this.playerInstance
      ?.play()
      .then(() => {
        // Success, play started
      })
      .catch(() => {
        // Browser didn't allowed the stream to play, inform that the stream was blocked
        if (onAutoPlayDenied) {
          onAutoPlayDenied(true);
        }
      });
  }

  setMuted(arg: boolean): void {
    this.playerInstance?.muted(arg);
    const { onMute } = this.props;
    if (onMute) {
      onMute(arg);
    }
  }

  isMuted(): boolean {
    return this.playerInstance?.muted() || false;
  }

  setLoop(arg: boolean): string | undefined {
    if (typeof arg !== 'undefined') {
      this.playerInstance?.loop(arg);
    }
    return this.playerInstance?.loop();
  }

  setPosition(position: Seconds, asset: Asset | undefined): boolean {
    let validPos: boolean;
    const ranges = this.getSeekableRange();

    let streamStart: Seconds = 0;
    if (ranges.isEpoch) {
      if (asset && AssetUtils.isEpg(asset)) {
        const start = asset.padding?.start
          ? TimeUtils.subtractDestructuredDuration(asset.startDate, asset.padding.start)
          : asset.startDate;
        streamStart = TimeUtils.toUnixEpoch(start);
      }
    }
    let normPos: Seconds = streamStart + position;

    if (normPos < ranges.start) {
      normPos = Math.ceil(ranges.start);
      validPos = false;
    } else if (normPos > ranges.end) {
      normPos = Math.floor(ranges.end);
      validPos = false;
    } else {
      normPos = Math.ceil(normPos);
      validPos = true;
    }

    if (PlatformUtils.getBrowser() === APP_BROWSERS.Safari && normPos === 0) {
      // hack for safari for ongoing streams
      normPos = 0.1;
    }

    this.playerInstance?.currentTime(normPos === 0 ? 1 : normPos);

    return validPos;
  }

  getPosition(): Seconds {
    return this.playerInstance?.currentTime() || 0;
  }

  duration(): Seconds {
    return this.playerInstance?.duration() || 0;
  }

  paused(): boolean | undefined {
    return this.playerInstance?.paused();
  }

  volume(volume?: number): number | undefined {
    if (volume) {
      return this.playerInstance?.volume(volume);
    }
    return this.playerInstance?.volume();
  }

  getSubtitles(): TextTrackList | [] {
    return this.playerInstance?.textTracks() || [];
  }

  getAudio(): AudioTrack[] | [] {
    return this.playerInstance?.audioTracks() || [];
  }

  getError(): NagraPlayerError {
    return this.playerInstance?.error();
  }

  getSeekableRange(): StreamRange {
    const timeRanges = this.playerInstance?.seekable();
    if (timeRanges?.length) {
      // Some streams return its range as an epoch, if the start of the stream range is bigger than 1 week treat it as an epoch
      // ( no stream range should come close to that value, according to the specs a program should have a max duration of 12h
      // although bigger programs were observed. 1 week give us a good error margin )
      return {
        start: timeRanges.start(0),
        end: timeRanges.end(0),
        isEpoch: timeRanges.start(0) > 7 * 24 * 60 * 60, // 1 week in seconds
      };
    }

    return DEFAULT_RANGE;
  }

  // UI

  render(): JSX.Element {
    const { videoElementRef: videoRef } = this.props;

    return (
      <div className="hero-wrapper-video" ref={videoRef} id="Nagra_videoWrapper">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video id={VIDEO_ELEMENT_ID} className="player-instance" crossOrigin="anonymous" muted playsInline />
      </div>
    );
  }
}

export default Nagra;
